import { useMqttSubscription } from '@hooks/mqtt/useMqttSubscription';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

function buildPingTopic(siteId: string, unitId?: string): string {
  return unitId
    ? `ems/site/${siteId}/unit/${unitId}/root/kW/float`
    : `ems/site/${siteId}/root/LocRemCtl/string`;
}

/**
 * Hook to detect if communication has been lost with a site or unit.
 * Uses the new Jotai/MQTT subscription mechanism.
 *
 * @param siteId - The site ID
 * @param dataRateTimeoutSeconds - Timeout in seconds before considering data as lost
 * @param unitId - Optional unit ID for unit-specific monitoring
 * @returns true if data loss is detected, false otherwise
 */
function useIsNoComms(
  siteId: string,
  dataRateTimeoutSeconds: number | undefined,
  unitId?: string,
): boolean {
  const topic = useMemo(() => buildPingTopic(siteId, unitId), [siteId, unitId]);

  const [dataLoss, setDataLoss] = useState<boolean>(false);
  const lastMessageTimestamp = useRef<number | null>(null);

  const handleMessage = useCallback(() => {
    lastMessageTimestamp.current = Date.now();
  }, []);

  useMqttSubscription(topic, {
    enabled: Boolean(topic),
    onMessage: handleMessage,
  });

  useEffect(() => {
    const checkDataLossInterval = setInterval(() => {
      if (lastMessageTimestamp.current === null) {
        lastMessageTimestamp.current = Date.now();
      } else {
        const isDataLost =
          dataRateTimeoutSeconds !== undefined &&
          dataRateTimeoutSeconds > 0 &&
          Date.now() - lastMessageTimestamp.current >
            dataRateTimeoutSeconds * 1000;
        setDataLoss(isDataLost);
      }
    }, 1000);

    return () => clearInterval(checkDataLossInterval);
  }, [dataRateTimeoutSeconds]);

  return dataLoss;
}

export default useIsNoComms;
